import api from '@/lib/api'

const buildVenueEndpoint = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const baseUrl = String(api.defaults.baseURL ?? '').trim().replace(/\/+$/, '')
  if (baseUrl.endsWith('/api')) {
    return `/venue${normalizedPath}`
  }
  return `/api/venue${normalizedPath}`
}

export type VenueSidebarConfigRecord = {
  id: number
  service_instance: number
  menu_items: unknown[]
  admin_menu_items: unknown[]
  is_events_enabled: boolean
  is_fundraising_enabled: boolean
  created_at?: string
  updated_at?: string
}

export type VenueSidebarConfigWritePayload = {
  service_instance?: number
  service_instance_id?: string | number
  menu_items: unknown[]
  admin_menu_items?: unknown[]
  is_events_enabled?: boolean
  is_fundraising_enabled?: boolean
}

type VenueSidebarListResponse =
  | VenueSidebarConfigRecord[]
  | {
    count?: number
    next?: string | null
    previous?: string | null
    results?: VenueSidebarConfigRecord[]
  }

const coerceSidebarConfigList = (payload: VenueSidebarListResponse): VenueSidebarConfigRecord[] => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (payload && Array.isArray(payload.results)) {
    return payload.results
  }

  return []
}

export type VenueTerminologyRecord = {
  id: number
  service_instance: number
  venue_type: string
  figure_label: string
  figure_plural: string
  service_label: string
  service_plural: string
  event_label: string
  event_plural: string
  member_label: string
  member_plural: string
  primary_language: string
  secondary_language?: string | null
  supported_languages?: string[]
  terminology?: {
    primary_language?: string
    secondary_language?: string | null
    supported_languages?: string[]
  }
}

type VenueTerminologyListResponse =
  | VenueTerminologyRecord[]
  | {
    count?: number
    results?: VenueTerminologyRecord[]
  }

const coerceTerminologyList = (payload: VenueTerminologyListResponse): VenueTerminologyRecord[] => {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.results)) return payload.results
  return []
}

export const venueApi = {
  listTerminology: async (serviceInstanceId: string | number) => {
    const response = await api.get<VenueTerminologyListResponse>(buildVenueEndpoint('/venue-terminology/'), {
      params: { service_instance_id: serviceInstanceId },
    })
    return coerceTerminologyList(response.data)
  },

  getTerminology: async (serviceInstanceId: string | number) => {
    const rows = await venueApi.listTerminology(serviceInstanceId)
    return rows[0] ?? null
  },

  createTerminology: async (
    payload: Partial<VenueTerminologyRecord> & {
      venue_type: string
      service_instance?: number
      service_instance_id?: string | number
    },
  ) => {
    const response = await api.post<VenueTerminologyRecord>(buildVenueEndpoint('/venue-terminology/'), payload)
    return response.data
  },

  updateTerminology: async (id: number, payload: Partial<VenueTerminologyRecord>) => {
    const response = await api.patch<VenueTerminologyRecord>(buildVenueEndpoint(`/venue-terminology/${id}/`), payload)
    return response.data
  },

  listSidebarConfigs: async (serviceInstanceId: string | number) => {
    const response = await api.get<VenueSidebarListResponse>(buildVenueEndpoint('/sidebar-config/'), {
      params: { service_instance_id: serviceInstanceId },
    })
    return coerceSidebarConfigList(response.data)
  },

  getSidebarConfig: async (serviceInstanceId: string | number) => {
    const configs = await venueApi.listSidebarConfigs(serviceInstanceId)
    return configs[0] ?? null
  },

  createSidebarConfig: async (payload: VenueSidebarConfigWritePayload) => {
    const response = await api.post<VenueSidebarConfigRecord>(buildVenueEndpoint('/sidebar-config/'), payload)
    return response.data
  },

  updateSidebarConfig: async (id: number, payload: Partial<VenueSidebarConfigWritePayload>) => {
    const response = await api.patch<VenueSidebarConfigRecord>(buildVenueEndpoint(`/sidebar-config/${id}/`), payload)
    return response.data
  },
}

export default venueApi
